/**
 * GELD — Geld & Financieel rulebook.
 *
 * Sourced from DEFAULT_POLICY (Geld entries) +
 * docs/audits/DIDACTISCH_AUDIT_GELD_2026.md + MODULE_AUTHORING_GUIDE §5.
 */

import type { ModuleRulebook } from './types'

export const GELD_RULEBOOK: ModuleRulebook = {
  moduleId: 'Geld',
  shortCode: 'GELD',
  displayName: 'Geld',

  didactics: {
    principles: [
      'euro_cent_onderscheid',
      'wisselgeld_logica',
      'procent_in_context',
      'budget_planning',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'geld',
    coreSummary:
      'Geld in contextopgaven: tellen, wisselen, terugrekenen, korting, BTW, budget en eenvoudige rente.',
  },

  notation: {
    requireCommaDecimals: true,
    requireEuroComma: true,
    forbiddenPatterns: [
      {
        pattern: '€\\d+\\.\\d{2}',
        label: 'Euro-bedrag met punt (gebruik €12,50)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'euro|cent|wisselgeld|korting|btw|budget|rente',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'wisselgeld',
      'korting',
      'btw',
      'percentage',
      'budget',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'money',
      'receipt',
      'budgetBar',
      'priceCompare',
      'barModel',
    ],
    familyVisualHints: [
      {
        familyIdPattern: '^GELD-(TELLEN|MAAK|WISSEL|WISSELEN)',
        preferredStimulus: 'money',
        note: 'Geld-tellen / wisselen vraagt om munten/biljetten visueel.',
      },
      {
        familyIdPattern: '^GELD-TERUG(REKENEN)?',
        preferredStimulus: 'money',
        preferredSupport: 'receipt',
        note: 'Wisselgeld terugrekenen: betaald bedrag in munten + bonnetje als context.',
      },
      {
        familyIdPattern: '^GELD-(BOODSCHAPPEN|KOOPJES|PRIJS-PER)',
        preferredStimulus: 'receipt',
        note: 'Bonnetjes voor boodschappen-context.',
      },
      {
        familyIdPattern: '^GELD-(BUDGET|SPAREN)',
        preferredSupport: 'budgetBar',
        note: 'Budget-balk om verdeling visueel te maken.',
      },
      {
        familyIdPattern: '^GELD-(KORTING|BTW)',
        preferredSupport: 'barModel',
        note: 'Strookmodel voor korting/BTW: oorspronkelijke prijs als geheel, korting/BTW als deel.',
      },
      {
        familyIdPattern: '^GELD-RENTE-PROC',
        preferredSupport: 'percentageBar',
        note: 'Rente als procent: percentageBar koppelt % aan euro-bedrag.',
      },
      {
        familyIdPattern: '^GELD-KOMMA',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Geld-notatie (€-komma-cent): regel-kaart als geheugensteun.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'money',
      'estimation_slider',
      'step_solver',
      'bar_model_part',
    ],
    customTypeRoles: [
      {
        type: 'bar_model_part',
        didacticRole: 'strategic',
        preferredDok: 2,
        appropriateTiers: { min: 7, max: 14 },
        reason:
          'Strookmodel bij korting/BTW: het kind leest het deel-bedrag af i.p.v. te rekenen — bouwt procent-intuïtie.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^GELD-TELLEN',
        preferredTypes: ['money', 'fill_in', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Geld tellen: munten/biljetten visueel optellen — money-renderer of exact bedrag invullen.',
      },
      {
        familyIdPattern: '^GELD-(MAAK|WISSEL|WISSELEN)',
        preferredTypes: ['money', 'fill_in', 'multiple_choice', 'step_solver'],
        discouragedTypes: ['true_false_series'],
        note: 'Geld maken/wisselen vraagt om actieve constructie of berekening — herkenning via TF is te oppervlakkig.',
      },
      {
        familyIdPattern: '^GELD-(KORTING|BTW|RENTE-PROC|PRIJS-PER)',
        preferredTypes: [
          'step_solver',
          'fill_in',
          'bar_model_part',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Korting/BTW/rente/prijs-per: meerstaps procent-berekening; strookmodel of step_solver maakt strategie expliciet.',
      },
      {
        familyIdPattern:
          '^GELD-(BUDGET|BOODSCHAPPEN|KOOPJES|SPAREN|TERUG|TERUGREKENEN)',
        preferredTypes: ['step_solver', 'fill_in', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Context-rekenen met geld: meerstaps in realistische scenario; step_solver houdt strategie expliciet.',
      },
      {
        familyIdPattern: '^GELD-KOMMA',
        preferredTypes: ['fill_in', 'multiple_choice'],
        note: 'Geld-notatie (€-komma-cent): nauwkeurige typing/keuze, geen TF.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'euro-cent-swap',
      label: 'Euro/cent verwisselen',
      description: '€2,50 als €250 of 250 cent als €25 interpreteren.',
    },
    {
      id: 'wrong-discount-direction',
      label: 'Korting verkeerde richting',
      description: 'Korting OPTELLEN bij de prijs i.p.v. aftrekken.',
    },
    {
      id: 'percentage-of-wrong-base',
      label: 'Percentage van verkeerde basis',
      description: 'BTW van bruto i.p.v. netto berekenen (of andersom).',
    },
  ],

  keyRules: [
    'Euro-notatie: €12,50 (komma, niet punt)',
    'Money-visual verplicht voor geld-telvragen',
    'Trap-distractor: euro/cent verwisselen',
    'BTW-tarief: 21% (actueel NL)',
    'Wisselgeld altijd vanuit het betaalde bedrag terugrekenen',
    'Strookmodel (barModel) bij korting/BTW-context — koppelt % en €',
  ],

  authoringGuideAnchor: '#5-geld--geld--financieel',
}
